import { useMemo } from 'react';
import { useListAssociationTypesQuery } from '@/store/api/associationsApi';
import { useListEntityTypesQuery } from '@/store/api/entityTypesApi';
import type { AssociationTypeRow, EntityTypeRow } from '@/types/domain';

function pluralLabel(namePlural: string | null | undefined, fallback: string): string {
  if (namePlural && namePlural.trim().length > 0) {
    return namePlural;
  }
  return fallback;
}

function pluralizeAssociationName(name: string): string {
  const separator = ' de ';
  const idx = name.indexOf(separator);
  if (idx <= 0) {
    return `${name}s`;
  }
  const head = name.slice(0, idx);
  const tail = name.slice(idx);
  if (head.endsWith('o')) {
    return `${head.slice(0, -1)}os${tail}`;
  }
  if (head.endsWith('a')) {
    return `${head.slice(0, -1)}as${tail}`;
  }
  return `${head}s${tail}`;
}

export interface ScopedTypeLabels {
  entityLabel: string;
  entityLabelPlural: string;
  associationLabel: string;
  associationLabelPlural: string;
  singleEntityType: EntityTypeRow | null;
  singleAssociationType: AssociationTypeRow | null;
  isLoading: boolean;
}

export function useScopedTypeLabels(): ScopedTypeLabels {
  const { data: entityTypes = [], isLoading: entityLoading } = useListEntityTypesQuery();
  const { data: associationTypes = [], isLoading: assocLoading } = useListAssociationTypesQuery();

  const activeEntityTypes = useMemo(
    () => entityTypes.filter((row) => row.active),
    [entityTypes],
  );
  const activeAssociationTypes = useMemo(
    () => associationTypes.filter((row) => row.active),
    [associationTypes],
  );

  const singleEntityType = activeEntityTypes.length === 1 ? activeEntityTypes[0] : null;
  const singleAssociationType = activeAssociationTypes.length === 1 ? activeAssociationTypes[0] : null;

  return {
    entityLabel: singleEntityType?.name ?? 'Entidad',
    entityLabelPlural: singleEntityType
      ? pluralLabel(singleEntityType.namePlural, 'Entidades')
      : 'Entidades',
    associationLabel: singleAssociationType?.name ?? 'Grupo',
    associationLabelPlural: singleAssociationType
      ? pluralizeAssociationName(singleAssociationType.name)
      : 'Grupos',
    singleEntityType,
    singleAssociationType,
    isLoading: entityLoading || assocLoading,
  };
}
